/**
 * Design Tokens — EINZIGE Quelle der Wahrheit für das visuelle System.
 *
 * Basis ist die ursprüngliche "azubi-web"-Vorlage (Neris-Schrift, Farbsystem).
 * Sowohl das MUI-Theme (theme.ts) als auch der CSS-:root-Block in
 * azubi-web-styles.css leiten sich von diesen Werten ab. Früher wurden TS und
 * CSS per Hand synchron gehalten (drift-anfällig); ein Guard-Test
 * (designTokens.consistency.test.ts) stellt nun sicher, dass der CSS-:root-Block
 * nicht mehr unbemerkt von hier abweicht.
 *
 * Mandantenabhängig (white-label) sind NUR primary/secondary — die kommen zur
 * Laufzeit aus dem Branding und überschreiben die Defaults unten. Alles andere
 * ist systemweit fix.
 */

export const tokens = {
    color: {
        // Marken-Defaults (white-label: zur Laufzeit pro Mandant überschrieben)
        primary: '#e72956',
        primaryLight: '#f26788',
        primaryDark: '#c21642',
        secondary: '#00db9b',
        secondaryLight: '#33e6b3',
        secondaryDark: '#00b37f',
        // Akzent-Dunkeltöne aus der Vorlage (Links/Alerts)
        infoDark: '#266d94', // --color-primary-dark (Link-Hover, alert-primary text)
        secondaryAccentDark: '#1dac80', // --color-secondary-dark (Buttons/Alerts)

        error: '#ff6d73',
        errorDark: '#994145',

        // Neutralskala
        neutral900: '#1f3240',
        neutral600: '#6c757d',
        neutral500: '#677e8b',
        neutral300: '#c8ced8',
        neutral200: '#e1e2e4',
        neutral100: '#f4f4f4',
        gold500: '#c0a15e',

        text: {
            primary: '#1f3240',
            secondary: '#677e8b',
        },
        background: {
            default: '#ffffff',
            paper: '#f4f4f4',
        },
        divider: '#dee2e6',

        // Formular-/Input-Akzente (Bootstrap-Look der MUI-Inputs)
        inputBorder: '#ced4da',
        inputFocusBorder: '#86b7fe',
        inputFocusShadow: 'rgba(13, 110, 253, 0.25)',

        // Avatar-Hintergrund (war bisher hartkodiert #c8ced8 in beiden Apps)
        avatarBg: '#c8ced8',
    },

    /**
     * Warn-Palette (WCAG-abgestimmt): graphische Töne vs. textfähige Töne.
     * main = Icon/Chip-Border (3:1 reicht), dark = Text/Buttons (4.87:1 auf weiß).
     */
    warn: {
        main: '#ed6c02',
        dark: '#9a6700',
        darkHover: '#7d5300',
        contrastText: '#ffffff',
        solidBg: '#ffda65',
        solidText: '#1f3240',
        surfaceBg: '#fff4d4',
        surfaceText: '#6b4e00',
        surfaceBorder: '#e8c766',
    },

    /**
     * Statistik-/Dashboard-Akzentfarben. Bisher im Admin-Dashboard hartkodiert
     * als Fremd-Palette ohne Token — hier zentralisiert, damit Kacheln in beiden
     * Apps konsistent dieselben Töne nutzen.
     */
    stat: {
        blue: '#2196f3',
        purple: '#9c27b0',
        deepOrange: '#ff5722',
        orange: '#ed6c02',
        teal: '#00db9b',
        gold: '#c0a15e',
    },

    /** Finanz-Kennzahlen (Gewinn/Verlust/offen) — bisher hartkodiert. */
    financial: {
        gain: '#4caf50',
        loss: '#f44336',
        pending: '#ff9800',
    },

    typography: {
        fontFamily: 'Neris, "Helvetica Neue", Arial, sans-serif',
        htmlFontSizePx: 18,
        h1: { fontSize: '3.25rem', fontWeight: 700, lineHeight: 1.2 },
        h2: { fontSize: '2.375rem', fontWeight: 700, lineHeight: 1.2 },
        h3: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.2 },
        h4: { fontSize: '1.375rem', fontWeight: 700, lineHeight: 1.2 },
        h5: { fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.2 },
        h6: { fontSize: '1.125rem', fontWeight: 700, lineHeight: 1.2 },
        body1: { fontSize: '1rem', lineHeight: 1.5 },
        button: { fontWeight: 600, textTransform: 'none' as const },
    },

    shape: {
        radiusSm: 6, // Buttons, Inputs (MUI shape.borderRadius)
        radiusMd: 12, // Cards
    },

    elevation: {
        card: '0 2px 8px rgba(0, 0, 0, 0.08)',
        cardHover: '0 4px 16px rgba(0, 0, 0, 0.12)',
        appBar: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
} as const;

/**
 * Abbildung der Tokens auf die CSS-Custom-Properties im :root von
 * azubi-web-styles.css. Single source: der Guard-Test vergleicht den CSS-Block
 * gegen diese Map, sodass CSS und TS nicht mehr auseinanderlaufen können.
 */
export const rootCssVars: Record<string, string> = {
    // Bootstrap-Overrides
    '--bs-primary': tokens.color.primary,
    '--bs-primary-rgb': '231, 41, 86',
    '--bs-secondary': tokens.color.secondary,
    '--bs-secondary-rgb': '0, 219, 155',
    '--bs-success': tokens.color.secondary,
    '--bs-success-rgb': '0, 219, 155',
    '--bs-danger': tokens.color.error,
    '--bs-danger-rgb': '255, 109, 115',
    '--bs-warning': tokens.warn.solidBg,
    '--bs-warning-rgb': '255, 218, 101',
    '--bs-info': tokens.color.neutral900,
    '--bs-info-rgb': '231, 41, 86',
    '--bs-gray-100': tokens.color.neutral100,
    '--bs-gray-200': tokens.color.neutral200,
    '--bs-gray-300': tokens.color.neutral300,
    '--bs-gray-500': tokens.color.neutral500,
    '--bs-gray-600': tokens.color.neutral600,
    '--bs-gray-900': tokens.color.neutral900,
    '--bs-body-font-family': tokens.typography.fontFamily,
    '--bs-body-color': tokens.color.neutral900,
    '--bs-body-color-rgb': '31, 50, 64',
    '--bs-body-bg': '#fff',
    '--bs-border-color': tokens.color.divider,
    '--bs-border-radius': '0.375rem',
    '--bs-border-radius-lg': '0.5rem',
    '--bs-link-color': tokens.color.neutral900,
    '--bs-link-hover-color': tokens.color.infoDark,
    // Custom-Tokens
    '--color-neutral-900': tokens.color.neutral900,
    '--color-neutral-500': tokens.color.neutral500,
    '--color-neutral-300': tokens.color.neutral300,
    '--color-neutral-200': tokens.color.neutral200,
    '--color-neutral-100': tokens.color.neutral100,
    '--color-gold-500': tokens.color.gold500,
    '--oa-warn-fg': tokens.warn.dark,
    '--oa-warn-solid': tokens.warn.solidBg,
    '--oa-warn-surface': tokens.warn.surfaceBg,
    '--oa-warn-on-surface': tokens.warn.surfaceText,
    '--oa-warn-border': tokens.warn.surfaceBorder,
    '--color-primary-dark': tokens.color.infoDark,
    '--color-secondary-dark': tokens.color.secondaryAccentDark,
};

export type DesignTokens = typeof tokens;
